/**
 * Types and functions for working with 3d rays
 * @module
 */
import type { Ray2 } from "./ray2Types";
import type { Vec3 } from "./vec3";
import { vec3Dist, vec3Dot, vec3Result, vec3Sub } from "./vec3";

export interface Ray3 extends Ray2 {
  originZ: number;
  dirZ: number;
}

export function ray3New(
  originX: number = 0,
  originY: number = 0,
  originZ: number = 0,
  dirX: number = 1,
  dirY: number = 0,
  dirZ: number = 0,
): Ray3 {
  return { originX, originY, originZ, dirX, dirY, dirZ };
}

export function ray3Eval(
  originX: number,
  originY: number,
  originZ: number,
  dirX: number,
  dirY: number,
  dirZ: number,
  t: number,
): Vec3 {
  vec3Result[0] = originX + dirX * t;
  vec3Result[1] = originY + dirY * t;
  vec3Result[2] = originZ + dirZ * t;
  return vec3Result;
}

export function ray3DotProject(
  originX: number,
  originY: number,
  originZ: number,
  dirX: number,
  dirY: number,
  dirZ: number,
  x: number,
  y: number,
  z: number,
): number {
  const [dx, dy, dz] = vec3Sub(x, y, z, originX, originY, originZ);
  return vec3Dot(dx, dy, dz, dirX, dirY, dirZ);
}

export function ray3Project(
  originX: number,
  originY: number,
  originZ: number,
  dirX: number,
  dirY: number,
  dirZ: number,
  x: number,
  y: number,
  z: number,
): Vec3 {
  const [dx, dy, dz] = vec3Sub(x, y, z, originX, originY, originZ);
  const dot = vec3Dot(dx, dy, dz, dirX, dirY, dirZ);
  return ray3Eval(originX, originY, originZ, dirX, dirY, dirZ, dot);
}

export function ray3Param(
  originX: number,
  originY: number,
  originZ: number,
  dirX: number,
  dirY: number,
  dirZ: number,
  x: number,
  y: number,
  z: number,
): number {
  const [dx, dy, dz] = vec3Sub(x, y, z, originX, originY, originZ);
  return vec3Dot(dx, dy, dz, dirX, dirY, dirZ);
}

export function ray3PerpDistance(
  originX: number,
  originY: number,
  originZ: number,
  dirX: number,
  dirY: number,
  dirZ: number,
  x: number,
  y: number,
  z: number,
): number {
  const [px, py, pz] = ray3Project(originX, originY, originZ, dirX, dirY, dirZ, x, y, z);
  return vec3Dist(px, py, pz, x, y, z);
}
